import type { Schemas } from "@acervo/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { CheckCircle2, ChevronDown, Lock, Play, UploadCloud } from "lucide-react";
import { useState } from "react";
import { Cover, ErrorBox, ProgressBar, Spinner, VisibilityBadge } from "../components/ui";
import { api, isAdmin, useSession } from "../lib/api";
import { clock, duration, errorMessage, percent, visibilityLabel } from "../lib/format";

const route = getRouteApi("/_app/cursos/$slug");

type Course = Schemas["CourseDetail"];
type Lesson = Course["modules"][number]["lessons"][number];

export const courseQuery = (slug: string) => ({
  queryKey: ["course", slug],
  queryFn: () => api.unwrap(api.client.GET("/v1/courses/{slug}", { params: { path: { slug } } })),
});

export function CoursePage() {
  const { slug } = route.useParams();
  const session = useSession();
  const q = useQuery(courseQuery(slug));
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  const c = q.data;

  const lessons = c.modules.flatMap((m) => m.lessons);
  const startId = c.resume_lesson_id ?? lessons.find((l) => l.playable)?.id;
  const started = lessons.some((l) => l.progress);

  return (
    <div className="space-y-8">
      <section className="grid gap-6 md:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          <Link to="/" className="text-xs text-muted hover:text-fg">
            ← Início
          </Link>
          <h1 className="text-3xl leading-tight font-bold sm:text-4xl">{c.title}</h1>
          {c.description && <p className="max-w-3xl whitespace-pre-line text-muted">{c.description}</p>}
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
            <span>
              {c.lessons} aulas · {duration(c.duration_s)}
            </span>
            {c.tags.map((t) => (
              <span key={t} className="badge bg-white/5 text-muted">
                {t}
              </span>
            ))}
            {isAdmin(session) && <VisibilityBadge course={c} />}
          </div>
          <div className="max-w-md space-y-1">
            <ProgressBar value={percent(c.completed, c.lessons)} />
            <p className="text-xs text-muted">
              {c.completed} de {c.lessons} aulas concluídas
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {startId && (
              <Link to="/aula/$lessonId" params={{ lessonId: startId }} className="btn-primary">
                <Play className="h-4 w-4 fill-current" /> {started ? "Continuar" : "Começar"}
              </Link>
            )}
            {isAdmin(session) && (
              <Link to="/console/envios" search={{ curso: c.id }} className="btn-ghost">
                <UploadCloud className="h-4 w-4" /> Adicionar aulas
              </Link>
            )}
          </div>
        </div>
        <Cover title={c.title} url={c.cover_url} className="hidden aspect-[4/3] w-full rounded-xl md:flex" />
      </section>

      {isAdmin(session) && <CourseAdmin course={c} />}

      <section className="space-y-3">
        {c.modules.map((m, i) => (
          <Module key={m.id} module={m} defaultOpen={i === 0 || m.lessons.some((l) => l.id === c.resume_lesson_id)} admin={isAdmin(session)} slug={c.slug} />
        ))}
      </section>
    </div>
  );
}

function Module({
  module,
  defaultOpen,
  admin,
  slug,
}: {
  module: Course["modules"][number];
  defaultOpen: boolean;
  admin: boolean;
  slug: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const done = module.lessons.filter((l) => l.progress?.completed).length;
  const total = module.lessons.reduce((s, l) => s + l.duration_s, 0);
  return (
    <div className="card overflow-hidden">
      <button
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-raised"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <ChevronDown className={`h-4 w-4 text-muted transition-transform ${open ? "" : "-rotate-90"}`} />
        <span className="flex-1 font-semibold">{module.title}</span>
        <span className="text-xs text-muted">
          {done}/{module.lessons.length} · {duration(total)}
        </span>
      </button>
      {open && (
        <ol className="border-t border-line">
          {module.lessons.map((l) => (
            <LessonRow key={l.id} lesson={l} admin={admin} slug={slug} />
          ))}
        </ol>
      )}
    </div>
  );
}

function LessonRow({ lesson: l, admin, slug }: { lesson: Lesson; admin: boolean; slug: string }) {
  const pct = l.progress ? (l.progress.completed ? 100 : percent(l.progress.position_s, l.duration_s)) : 0;
  const body = (
    <>
      <span className="w-6 shrink-0 text-center">
        {l.progress?.completed ? (
          <CheckCircle2 className="h-4 w-4 text-ok" aria-label="Concluída" />
        ) : !l.playable ? (
          <Lock className="h-4 w-4 text-muted" aria-label="Bloqueada" />
        ) : (
          <span className="font-mono text-xs text-muted">{l.position}</span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm ${l.playable ? "" : "text-muted"}`}>{l.title}</span>
        {pct > 0 && pct < 100 && <ProgressBar value={pct} className="mt-1 max-w-48" />}
      </span>
      {l.is_preview && <span className="badge bg-accent/15 text-accent">Amostra</span>}
      <span className="font-mono text-xs text-muted">{clock(l.duration_s)}</span>
    </>
  );
  return (
    <li className="flex items-center border-b border-line/60 last:border-0">
      {l.playable ? (
        <Link
          to="/aula/$lessonId"
          params={{ lessonId: l.id }}
          className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 hover:bg-raised"
        >
          {body}
        </Link>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5">{body}</div>
      )}
      {admin && <PreviewToggle lesson={l} slug={slug} />}
    </li>
  );
}

function PreviewToggle({ lesson, slug }: { lesson: Lesson; slug: string }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (is_preview: boolean) =>
      api.unwrap(
        api.client.PUT("/v1/admin/lessons/{id}/preview", { params: { path: { id: lesson.id } }, body: { is_preview } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["course", slug] }),
  });
  return (
    <label className="flex shrink-0 items-center gap-1.5 px-3 text-[11px] text-muted" title="Amostra grátis para alunos">
      <input
        type="checkbox"
        className="accent-accent"
        checked={lesson.is_preview}
        disabled={m.isPending}
        onChange={(e) => m.mutate(e.target.checked)}
      />
      amostra
    </label>
  );
}

function CourseAdmin({ course }: { course: Course }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(course.title);
  const [description, setDescription] = useState(course.description);
  const [visibility, setVisibility] = useState(course.visibility);
  const m = useMutation({
    mutationFn: () =>
      api.unwrap(
        api.client.PATCH("/v1/admin/courses/{id}", {
          params: { path: { id: course.id } },
          body: { title, description, visibility },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["course", course.slug] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["admin", "courses"] });
    },
  });
  const dirty = title !== course.title || description !== course.description || visibility !== course.visibility;
  const thirdParty = course.rights === "third_party";

  return (
    <details className="card p-4">
      <summary className="cursor-pointer text-sm font-semibold text-accent">Editar curso (console)</summary>
      <form
        className="mt-4 grid gap-4 md:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <div>
          <label className="label" htmlFor="c-title">
            Título
          </label>
          <input id="c-title" className="field" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="c-vis">
            Visibilidade
          </label>
          <select
            id="c-vis"
            className="field"
            value={visibility}
            disabled={thirdParty}
            onChange={(e) => setVisibility(e.target.value as Course["visibility"])}
          >
            {(["private", "unlisted", "published"] as const).map((v) => (
              <option key={v} value={v}>
                {visibilityLabel[v]}
              </option>
            ))}
          </select>
          {thirdParty && (
            <p className="mt-1 text-xs text-muted">Conteúdo de terceiros fica sempre privado: só você vê.</p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="label" htmlFor="c-desc">
            Descrição
          </label>
          <textarea
            id="c-desc"
            className="field min-h-24"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-3 md:col-span-2">
          <button className="btn-primary" disabled={!dirty || m.isPending}>
            {m.isPending ? "Salvando…" : "Salvar"}
          </button>
          {m.isSuccess && !dirty && <span className="text-xs text-ok">Salvo</span>}
          {m.isError && <span className="text-xs text-danger">{errorMessage(m.error)}</span>}
        </div>
      </form>
    </details>
  );
}
